import React, { useEffect } from 'react';
import '../styles/components/button.css';
import { motion } from 'framer-motion';
import { Beaker, Brain, Users, ArrowRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { statsStorage, localSettings } from '../services/persistentStorage';

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  // 记录页面访问
  useEffect(() => {
    const recordVisit = async () => {
      try {
        await statsStorage.recordVisit();
      } catch (error) {
        console.error('记录访问失败:', error);
      }
    };
    recordVisit();
  }, []); // 空依赖数组确保只在组件挂载时执行一次

  const handleAboutDeveloper = () => {
    navigate('/about-developer');
  };

  const handleEmotionalStart = async () => {
    try {
      // 检查是否为开放模式
      const isOpenMode = await localSettings.getTestModeOpen('emotional-memory-pattern');
      
      if (isOpenMode) {
        // 开放模式：直接跳转到测评页面
        navigate('/emotional-memory-pattern');
      } else {
        // 验证模式：跳转到测评码输入页面，使用统一的参数形式
        navigate('/test-code?type=emotional-memory-pattern');
      }
    } catch (error) {
      console.error('检查开放模式失败:', error);
      // 出错时默认使用验证模式
      navigate('/test-code?type=emotional-memory-pattern');
    }
  };

  const handleBigFiveStart = async () => {
    try {
      // 检查是否为开放模式
      const isOpenMode = await localSettings.getTestModeOpen('big-five-personality');
      
      if (isOpenMode) {
        // 开放模式：直接跳转到测评页面
        navigate('/big-five-personality');
      } else {
        // 验证模式：跳转到测评码输入页面
        navigate('/test-code?type=big-five-personality');
      }
    } catch (error) {
      console.error('检查开放模式失败:', error);
      // 出错时默认使用验证模式
      navigate('/test-code?type=big-five-personality');
    }
  };

  const handleTalentTestStart = () => {
    navigate('/talent-test-intro');
  };

  // 首页需要用到的一些常量数据
  const principleCards = [
    {
      icon: <Sparkles className="w-8 h-8 text-[#8A7CB4]" />,
      title: "语义洞察",
      description: "传统测评只能把你装进一个既有的框里。大语言模型天生擅长理解语义、整合信息，能从你的回答里读出那些用固定分类装不下的东西"
    },
    {
      icon: <Brain className="w-8 h-8 text-[#F5B6C1]" />,
      title: "有效框架",
      description: "模型的能力能发挥多少，取决于你给它什么。我们精心设计了问卷和提示词，提供有效的提炼框架，让这个过程有结构、有方向"
    },
    {
      icon: <Users className="w-8 h-8 text-[#3A7D78]" />,
      title: "真人辅导",
      description: "如果你想把测评进一步用起来，可以预约一对一咨询，由人来帮你把测评报告里的内容落到你自己的具体处境和问题上"
    }
  ];

  return (
    <div className="min-h-screen bg-[#3A7D78] text-white">
      {/* 导航栏 */}
      <nav className="fixed top-0 left-0 right-0 bg-[#3A7D78]/95 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <Beaker className="h-8 w-8 text-[#F5B6C1]" />
              <span className="text-xl font-bold text-white">人格向量实验室</span>
            </div>
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/personality-dictionary')}
                className="bg-transparent text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                词典中心
              </button>
              <button 
                onClick={handleAboutDeveloper}
                className="bg-[#F5B6C1] text-white px-4 py-2 rounded-lg hover:bg-[#F5B6C1]/90 transition-colors"
              >
                了解开发者
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* 主视觉区域 */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              探索
              <span className="text-[#F5B6C1]"> 人格 </span>
              的未知维度
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              找到自我描述和成长变化的更多可能
            </p>
          </motion.div>
        </div>
      </section>

      {/* 游戏入口区 */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
                whileHover={{ scale: 1.02 }}
                className="bg-white/10 rounded-2xl p-8 border border-white/20 cursor-pointer shadow-[0_8px_32px_rgba(0,0,0,0.1)]"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Users className="h-8 w-8 text-[#F5B6C1]" />
                  <h3 className="text-2xl font-bold text-white">情绪记忆与构型</h3>
                </div>
                <p className="text-white/80 mb-6">
                  情绪分布是否暗示人格？参与一场情感模式的探索
                </p>
                <button 
                  onClick={handleEmotionalStart}
                  className="flex items-center gap-2 text-[#F5B6C1] hover:text-white transition-colors"
                >
                  立即体验 <ArrowRight className="h-5 w-5" />
                </button>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="bg-white/10 rounded-2xl p-8 border border-white/20 cursor-pointer shadow-[0_8px_32px_rgba(0,0,0,0.1)]"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Sparkles className="h-8 w-8 text-purple-400" />
                  <h3 className="text-2xl font-bold text-white">大五人格测评</h3>
                </div>
                <p className="text-white/80 mb-6">
                  定位你的开放性、尽责性、外向性、宜人性和神经质特征
                </p>
                <button 
                  onClick={handleBigFiveStart}
                  className="flex items-center gap-2 text-purple-400 hover:text-white transition-colors"
                >
                  开始测评 <ArrowRight className="h-5 w-5" />
                </button>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="bg-white/10 rounded-2xl p-8 border border-white/20 cursor-pointer shadow-[0_8px_32px_rgba(0,0,0,0.1)]"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Users className="h-8 w-8 text-yellow-400" />
                  <h3 className="text-2xl font-bold text-white">34才干（团队）定制版</h3>
                </div>
                <p className="text-white/80 mb-6">
                  发现你的主导才干，解锁团队协作的潜力
                </p>
                <button 
                  onClick={handleTalentTestStart}
                  className="flex items-center gap-2 text-yellow-400 hover:text-white transition-colors"
                >
                  开始测评 <ArrowRight className="h-5 w-5" />
                </button>
              </motion.div>
          </div>
        </div>
      </section>

      {/* 实验室理念 */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            实验室理念
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {principleCards.map((card, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="bg-white/5 rounded-xl p-6"
              >
                <div className="mb-4">{card.icon}</div>
                <h3 className="text-xl font-bold text-white mb-3">
                  {card.title}
                </h3>
                <p className="text-white/80">{card.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* 管理员入口 - 隐蔽链接 */}
      <div className="text-center py-8">
        <button
          onClick={() => navigate('/admin')}
          className="text-white/30 hover:text-white/60 text-xs transition-colors"
        >
          ·
        </button>
      </div>
    </div>
  );
};

export default HomePage;